import { api } from "./api";
import {setAccessToken} from "../utils/userData";
import {cacheTags} from "./cacheTags";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    findReplacementResponsesByReplacementRequestId: build.query<
      FindReplacementResponsesByReplacementRequestIdApiResponse,
      FindReplacementResponsesByReplacementRequestIdApiArg
    >({
      query: (queryArg) => ({
        url: `/replacement-responses`,
        params: { replacementRequestId: queryArg.replacementRequestId },
      }),
    }),
    createReplacementResponse: build.mutation<
      CreateReplacementResponseApiResponse,
      CreateReplacementResponseApiArg
    >({
      query: (queryArg) => ({
        url: `/replacement-responses`,
        method: "POST",
        params: { tokenId: queryArg.tokenId },
      }),
    }),
    findAllReplacementRequest: build.query<
      FindAllReplacementRequestApiResponse,
      FindAllReplacementRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/replacement-requests`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          sort: queryArg.sort,
          replacementStatus: queryArg.replacementStatus,
        },
      }),
      providesTags: () => [cacheTags.REQUESTS]
    }),
    createReplacementRequest: build.mutation<
      CreateReplacementRequestApiResponse,
      CreateReplacementRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/replacement-requests`,
        method: "POST",
        body: queryArg.replacementRequestDto,
      }),
    }),
    confirmReplacementRequest: build.mutation<
      ConfirmReplacementRequestApiResponse,
      ConfirmReplacementRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/replacement-requests/${queryArg.replacementRequestId}`,
        method: "POST",
      }),
    }),
    completeReplacementRequest: build.mutation<
      CompleteReplacementRequestApiResponse,
      CompleteReplacementRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/replacement-requests/${queryArg.replacementRequestId}`,
        method: "DELETE",
      }),
      invalidatesTags: [cacheTags.REQUESTS]
    }),
    generateToken: build.mutation<
      GenerateTokenApiResponse,
      GenerateTokenApiArg
    >({
      query: (queryArg) => ({
        url: `/authentication`,
        method: "POST",
        headers: { authorization: queryArg.authorization },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.accessToken !== undefined) {
            setAccessToken(data.accessToken);
          }

        } catch (error) {
          console.error(error);
        }
      }
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type FindReplacementResponsesByReplacementRequestIdApiResponse =
  /** status 200 OK */ ReplacementResponse[];
export type FindReplacementResponsesByReplacementRequestIdApiArg = {
  replacementRequestId: string;
};
export type CreateReplacementResponseApiResponse =
  /** status 200 OK */ ReplacementResponse;
export type CreateReplacementResponseApiArg = {
  tokenId: string;
};
export type FindAllReplacementRequestApiResponse =
  /** status 200 OK */ PageReplacementRequest;
export type FindAllReplacementRequestApiArg = {
  /** Zero-based page index (0..N) */
  page?: number;
  /** The size of the page to be returned */
  size?: number;
  /** Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported. */
  sort?: string[];
  replacementStatus: "NEW" | "WAITING_FOR_REPLACEMENT" | "COMPLETED";
};
export type CreateReplacementRequestApiResponse =
  /** status 200 OK */ ReplacementRequest;
export type CreateReplacementRequestApiArg = {
  replacementRequestDto: ReplacementRequestDto;
};
export type ConfirmReplacementRequestApiResponse = unknown;
export type ConfirmReplacementRequestApiArg = {
  replacementRequestId: string;
};
export type CompleteReplacementRequestApiResponse = unknown;
export type CompleteReplacementRequestApiArg = {
  replacementRequestId: string;
};
export type GenerateTokenApiResponse =
  /** status 200 OK */ AuthenticationResponse;
export type GenerateTokenApiArg = {
  authorization: string;
};
export type ReplacementResponse = {
  replacementRequestId?: string;
  refereeId?: number;
  refereeFullName?: string;
};
export type ReplacementRequest = {
  id?: string;
  matchId?: number;
  matchUrl?: string;
  matchName?: string;
  refereeId?: number;
  refereeFullName?: string;
  replacementType?: "WARSAW" | "RADOM";
  replacementStatus?: "NEW" | "WAITING_FOR_REPLACEMENT" | "COMPLETED";
  additionalInformation?: string;
  createdDate?: string;
};
export type SortObject = {
  empty?: boolean;
  unsorted?: boolean;
  sorted?: boolean;
};
export type PageableObject = {
  offset?: number;
  sort?: SortObject;
  unpaged?: boolean;
  paged?: boolean;
  pageNumber?: number;
  pageSize?: number;
};
export type PageReplacementRequest = {
  totalPages?: number;
  totalElements?: number;
  first?: boolean;
  last?: boolean;
  size?: number;
  content?: ReplacementRequest[];
  number?: number;
  sort?: SortObject;
  pageable?: PageableObject;
  numberOfElements?: number;
  empty?: boolean;
};
export type ReplacementRequestDto = {
  matchUrl: string;
  refereeEmail: string;
  replacementType?: "WARSAW" | "RADOM";
  additionalInformation?: string;
};
export type AuthenticationResponse = {
  accessToken?: string;
};
export const {
  useFindReplacementResponsesByReplacementRequestIdQuery,
  useCreateReplacementResponseMutation,
  useFindAllReplacementRequestQuery,
  useCreateReplacementRequestMutation,
  useConfirmReplacementRequestMutation,
  useCompleteReplacementRequestMutation,
  useGenerateTokenMutation
} = injectedRtkApi;
